import React from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as PdfIcon } from '../../../assets/icons/export-pdf.svg';
import { InvoiceInterface } from '../../../interface/invoice-interface';
import MyButton from '../../../shared-components/button/Button';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import FormatDuration from '../../../shared-functions/FormatDuration';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import downloadSingleInvoicePdf from '../utils/generateInvoicePdf';
import '../scss/invoice-side-panel.scss';

/**
 * Properties for InvoiceSidePanel component.
 */
interface InvoiceSidePanelProperties {
  invoice: InvoiceInterface;
  onClose?: () => void;
}

/**
 * Helper to convert duration string (e.g. "HH:MM:SS" or seconds) to total seconds.
 * @param {string | number} [duration] - The duration value.
 * @returns {number} The total seconds.
 */
const parseDurationToSeconds = (duration?: string | number): number => {
  if (!duration) return 0;
  if (typeof duration === 'number') return duration;
  if (typeof duration === 'string' && duration.includes(':')) {
    const parts = duration.split(':').map((p) => Number(p) || 0);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
  }
  const numericValue = Number(duration);
  return !Number.isNaN(numericValue) ? numericValue : 0;
};

/**
 * InvoiceSidePanel component displaying full document format and PDF download button.
 * @param {InvoiceSidePanelProperties} properties - Component properties.
 * @returns {React.JSX.Element} The rendered component.
 */
function InvoiceSidePanel({ invoice, onClose }: InvoiceSidePanelProperties) {
  const { t } = useTranslation('invoice');

  const currencyString = (invoice.currency || 'INR').toUpperCase();
  const details = invoice.chargingDetails;
  const breakdown = details?.costBreakdown;

  const consumptionCost = breakdown?.consumptionCost ?? details?.invoiceAmountPaid;
  const platformFee = breakdown?.platformFee;
  const sessionFee = breakdown?.sessionFee;
  const totalAmountPaid = details?.invoiceAmountPaid || 0;

  /**
   * Handle PDF download click.
   */
  const handleDownload = () => {
    downloadSingleInvoicePdf(invoice);
  };

  return (
    <div className="invoice-side-panel-wrap">
      <div className="invoice-document-card">
        <div className="doc-header">
          <div className="doc-title">{t('taxInvoice', { defaultValue: 'TAX INVOICE' })}</div>
          <div className="doc-meta">
            <span className="inv-num">{invoice.invoiceNumber || '-'}</span>
            <span>
              {t('date', { defaultValue: 'Date' })}:{' '}
              {invoice.invoiceDate ? ConvertUTCtoDateTime(invoice.invoiceDate) : '-'}
            </span>
            <span>
              {t('financialYear', { defaultValue: 'FY' })}: {invoice.financialYear || '-'}
            </span>
          </div>
        </div>

        <div className="doc-section-grid">
          <div className="info-box">
            <div className="box-title">{t('invoiceFrom', { defaultValue: 'Invoice From' })}</div>
            <div className="box-row">
              <span className="label">{t('name', { defaultValue: 'Name' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.invoiceFrom?.name || 'Energic'}</span>
            </div>
            <div className="box-row">
              <span className="label">{t('email', { defaultValue: 'Email' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.invoiceFrom?.email || '-'}</span>
            </div>
            <div className="box-row">
              <span className="label">{t('gstin', { defaultValue: 'GSTIN' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.invoiceFrom?.gstin || '-'}</span>
            </div>
            <div className="box-row">
              <span className="label">{t('address', { defaultValue: 'Address' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.invoiceFrom?.billingAddress || '-'}</span>
            </div>
          </div>

          <div className="info-box">
            <div className="box-title">{t('invoiceTo', { defaultValue: 'Invoice To' })}</div>
            <div className="box-row">
              <span className="label">{t('name', { defaultValue: 'Name' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.invoiceTo?.name || '-'}</span>
            </div>
            <div className="box-row">
              <span className="label">{t('email', { defaultValue: 'Email' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.invoiceTo?.email || '-'}</span>
            </div>
            <div className="box-row">
              <span className="label">{t('mobile', { defaultValue: 'Mobile' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.invoiceTo?.mobile || '-'}</span>
            </div>
            <div className="box-row">
              <span className="label">{t('location', { defaultValue: 'Location' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.invoiceTo?.chargingLocation || '-'}</span>
            </div>
          </div>
        </div>

        <div className="doc-section-grid">
          <div className="info-box">
            <div className="box-title">{t('chargerInfo', { defaultValue: 'Charger Info' })}</div>
            <div className="box-row">
              <span className="label">{t('stationId', { defaultValue: 'Station ID' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.chargerInfo?.stationId || '-'}</span>
            </div>
            <div className="box-row">
              <span className="label">{t('chargerId', { defaultValue: 'Charger ID' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.chargerInfo?.chargerId || '-'}</span>
            </div>
            <div className="box-row">
              <span className="label">{t('connector', { defaultValue: 'Connector' })}</span>
              <span className="colon">:</span>
              <span className="val">
                {invoice.chargerInfo?.connectorType || '-'} (ID:{' '}
                {invoice.chargerInfo?.connectorId || '-'})
              </span>
            </div>
            <div className="box-row">
              <span className="label">{t('rating', { defaultValue: 'Rating' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.chargerInfo?.connectorRating || '-'}</span>
            </div>
          </div>

          <div className="info-box">
            <div className="box-title">{t('sessionDetails', { defaultValue: 'Session Details' })}</div>
            <div className="box-row">
              <span className="label">{t('sessionId', { defaultValue: 'Session ID' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.sessionId || '-'}</span>
            </div>
            <div className="box-row">
              <span className="label">{t('transactionId', { defaultValue: 'Transaction ID' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.transactionId || '-'}</span>
            </div>
            <div className="box-row">
              <span className="label">{t('duration', { defaultValue: 'Duration' })}</span>
              <span className="colon">:</span>
              <span className="val">
                {FormatDuration(
                  parseDurationToSeconds(invoice.chargingDetails?.chargingDuration),
                  '-',
                )}
              </span>
            </div>
            <div className="box-row">
              <span className="label">{t('energyUsed', { defaultValue: 'Energy Used' })}</span>
              <span className="colon">:</span>
              <span className="val">{invoice.chargingDetails?.energyUsed || 0} kWh</span>
            </div>
          </div>
        </div>

        <div className="cost-table-wrap">
          <table className="cost-table">
            <thead>
              <tr>
                <th>{t('item', { defaultValue: 'Item / Particulars' })}</th>
                <th>{t('amount', { defaultValue: 'Amount' })}</th>
              </tr>
            </thead>
            <tbody>
              {consumptionCost !== undefined && (
                <tr>
                  <td>{t('consumptionCost', { defaultValue: 'Consumption Cost' })}</td>
                  <td>
                    {ToFixedValue(consumptionCost)} {currencyString}
                  </td>
                </tr>
              )}
              {platformFee !== undefined && (
                <tr>
                  <td>{t('platformFee', { defaultValue: 'Platform Fee' })}</td>
                  <td>
                    {ToFixedValue(platformFee)} {currencyString}
                  </td>
                </tr>
              )}
              {sessionFee !== undefined && (
                <tr>
                  <td>{t('sessionFee', { defaultValue: 'Session Fee' })}</td>
                  <td>
                    {ToFixedValue(sessionFee)} {currencyString}
                  </td>
                </tr>
              )}
              <tr className="total-row">
                <td>{t('totalPaid', { defaultValue: 'Total Amount Paid' })}</td>
                <td>
                  {ToFixedValue(totalAmountPaid)} {currencyString}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="invoice-bottom-actions">
        <button type="button" onClick={handleDownload} className="pdf-download-btn">
          <PdfIcon />
          <span>{t('downloadPdf', { defaultValue: 'Download PDF' })}</span>
        </button>
        {onClose && (
          <MyButton
            label={t('close', { defaultValue: 'Close' })}
            buttonType="outline"
            onClickFunc={onClose}
          />
        )}
      </div>
    </div>
  );
}

InvoiceSidePanel.defaultProps = {
  onClose: undefined,
};

export default InvoiceSidePanel;
